import { FormCommonProps } from "src/components/common/forms/types.ts";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import i18n from "i18next";
import { AavoForm, AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import { DeliverySourcePickingApi } from "src/api/generated/erp/delivery/api/deliverySourcePickingApi.ts";
import { warehousePickingObjectToDeliveryPackageLineSource } from "src/components/views/erp/delivery/deliveryPackageLineSourceUtils.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AggregatedPickingLocation } from "src/api/generated/erp/delivery/service/aggregatedPickingLocation.ts";
import { distributePickingQuantity } from "src/components/views/erp/delivery/deliveryPackage/picking/distributePickingQuantity.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface AggregatedDeliveryPackagePickingFormProps extends FormCommonProps<void> {
	aggregatedPickingLocations: AggregatedPickingLocation[];
	deliveryPackageId: number;
}

interface FormValues {
	aggregatedPickingLocations: AggregatedPickingLocationFormValues[];
}

interface AggregatedPickingLocationFormValues extends AggregatedPickingLocation {
	quantityToPickInBaseUnit: number;
}

export const AggregatedDeliveryPackagePickingForm = (props: AggregatedDeliveryPackagePickingFormProps) => {
	const { aggregatedPickingLocations, deliveryPackageId, onCompleted, onFormEdited } = props;

	return (
		<AavoForm<FormValues, void>
			defaultValues={{
				aggregatedPickingLocations: aggregatedPickingLocations.map((location) => ({
					...location,
					quantityToPickInBaseUnit: location.reservedQuantity,
				})),
			}}
			render={(params) => <FormContent {...props} {...params} />}
			agentContext={{
				key: defineAgentUiContextKey("erp.delivery.aggregatedDeliveryPackagePicking.form"),
				title: i18n.t("pick"),
				data: {
					deliveryPackageId,
					partIds: aggregatedPickingLocations.map((location) => location.partId),
				},
			}}
			submit={async (values) => {
				const parts = values.aggregatedPickingLocations.flatMap((location) =>
					distributePickingQuantity(location.quantityToPickInBaseUnit, location.sourceLocations).map((pick) => ({
						source: warehousePickingObjectToDeliveryPackageLineSource({
							objectType: pick.objectType,
							objectId: pick.objectId,
						}),
						quantityToPickInBaseUnit: pick.quantityToPickInBaseUnit,
						partWarehouseLocationId: pick.partWarehouseLocationId,
					})),
				);

				await DeliverySourcePickingApi.pickPartsToPackage({
					deliveryPackageId: deliveryPackageId,
					parts: parts,
				});
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
		/>
	);
};

interface FormContentProps extends AggregatedDeliveryPackagePickingFormProps, AavoFormContentParams<FormValues> {}

const FormContent = (props: FormContentProps) => {
	const { aggregatedPickingLocations } = props;

	return (
		<VerticalBox>
			{aggregatedPickingLocations.map((location, index) => (
				<SingleLocationForm
					key={createAggregatedLocationKey(location)}
					index={index}
					aggregatedPickingLocation={location}
					{...props}
				/>
			))}
		</VerticalBox>
	);
};

interface SingleLocationFormSectionProps extends FormContentProps {
	index: number;
	aggregatedPickingLocation: AggregatedPickingLocation;
}

const SingleLocationForm = ({
	aggregatedPickingLocation: { locationCode, partUnit, configurationOrPartDescription },
	control,
	index,
}: SingleLocationFormSectionProps) => {
	return (
		<Box
			sx={{
				borderBottom: "1px solid",
				paddingBottom: 2,
				marginBottom: 2,
				paddingX: 1,
				borderColor: "divider",
				display: "flex",
				gap: 2,
				flexDirection: {
					xs: "column",
					sm: "row",
				},
			}}
		>
			<Typography
				variant={"h6"}
				sx={{
					flex: 1,
				}}
			>
				{configurationOrPartDescription}
			</Typography>
			<VerticalBox sx={{ gap: 1 }}>
				<FormNumberField
					key={"quantityToReserveInWarehouseUnit"}
					control={control}
					name={`aggregatedPickingLocations.${index}.quantityToPickInBaseUnit`}
					label={i18n.t("quantity_with_unit", { unit: partUnit })}
				/>
				<Typography key={"locationCode"}>{`${i18n.t("warehouse_location")} ${locationCode ?? "-"}`}</Typography>
			</VerticalBox>
		</Box>
	);
};

function createAggregatedLocationKey(location: AggregatedPickingLocation): string {
	return `${location.partId}_${location.partWarehouseLocationId}_${location.configurationContentHash ?? "null"}`;
}
